import { ColorModeOptions } from '@gui/constants/ColorModeOptions';
import type { ColorScheme, LineColor } from '@visualizer/types';
import { AllColorScheme, PreProcessingTypes } from '@visualizer/types';

// =====================================
// NEW COLOR SCHEMES
// =====================================

// Common Color Scheme values:
const CommonOptions = {
    // Project Sketch:
    [PreProcessingTypes.PROJECT_SKETCH_FLOOR]: {
        standard: { color: 0xc2c2c2, colorStroke: 0x545454 },
        render: { color: 0xc2c2c2, colorStroke: 0x545454 },
    },
    [PreProcessingTypes.PROJECT_SKETCH_WALL]: {
        standard: { color: 0xa6a6a6, colorStroke: 0x545454 },
        render: { color: 0xa6a6a6, colorStroke: 0x545454 },
    },
    [PreProcessingTypes.PROJECT_SKETCH_ROOF]: {
        standard: { color: 0x808080, colorStroke: 0x545454 },
        render: { color: 0x808080, colorStroke: 0x545454 },
    },

    // Wind Station:
    WIND_STATION_BOX: 0xb7b7b7,
};

const ColoredColorScheme: AllColorScheme = {
    // Sky:
    SKY: 0xd6ecff,

    // Ground:
    [PreProcessingTypes.GROUND]: {
        standard: { color: 0xf7f7f7, colorStroke: 0xd9d9d9 },
        render: { color: 0xf7f7f7, colorStroke: 0xd9d9d9 },
    },
    [PreProcessingTypes.GROUND_GREEN]: {
        standard: { color: 0xd9ead3, colorStroke: 0x93c47d },
        render: { color: 0xd9ead3, colorStroke: 0x93c47d },
    },
    [PreProcessingTypes.GROUND_LAKE]: {
        standard: { color: 0x71a7dc, colorStroke: 0x3d85c6 },
        render: { color: 0x71a7dc, colorStroke: 0x3d85c6 },
    },
    [PreProcessingTypes.GROUND_RIVER]: {
        standard: { color: 0x71a7dc, colorStroke: 0x3d85c6 },
        render: { color: 0x71a7dc, colorStroke: 0x3d85c6 },
    },
    [PreProcessingTypes.GROUND_CUSTOM_TARGET]: {
        standard: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
        render: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
    },

    // Buildings:
    [PreProcessingTypes.SWISSTOPO_FLOOR]: {
        standard: { color: 0xe1e1e1, colorStroke: 0x887d5a },
        render: { color: 0xe1e1e1, colorStroke: 0x887d5a },
    },
    [PreProcessingTypes.SWISSTOPO_WALL]: {
        standard: { color: 0xfff2cc, colorStroke: 0x887d5a },
        render: { color: 0xfff2cc, colorStroke: 0x998d65 },
    },
    [PreProcessingTypes.SWISSTOPO_ROOF]: {
        standard: { color: 0xda5555, colorStroke: 0xc04e4e },
        render: { color: 0xda5555, colorStroke: 0xc04e4e },
    },

    // Trees:
    [PreProcessingTypes.TREES]: {
        standard: { color: 0x6aa84f, colorStroke: 0x38761d },
        render: { color: 0x6aa84f, colorStroke: 0x38761d },
    },

    // Custom Obstructions:
    [PreProcessingTypes.CUSTOM_BUILDINGS_ROOF]: {
        standard: { color: 0xc04e4e, colorStroke: 0x984242 },
        render: { color: 0xc04e4e, colorStroke: 0x984242 },
    },
    [PreProcessingTypes.CUSTOM_BUILDINGS_WALL]: {
        standard: { color: 0xffebb3, colorStroke: 0x897d57 },
        render: { color: 0xffebb3, colorStroke: 0x897d57 },
    },
    [PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR]: {
        standard: { color: 0xb0b0b0, colorStroke: 0x897d57 },
        render: { color: 0xb0b0b0, colorStroke: 0x897d57 },
    },

    // Project Sketch and Wind Station:
    ...CommonOptions,
};

const DarkColorScheme: AllColorScheme = {
    // Sky:
    SKY: 0x000000,

    // Ground:
    [PreProcessingTypes.GROUND]: {
        standard: { color: 0x434343, colorStroke: 0x222222 },
        render: { color: 0x434343, colorStroke: 0x222222 },
    },
    [PreProcessingTypes.GROUND_GREEN]: {
        standard: { color: 0x666666, colorStroke: 0x999999 },
        render: { color: 0x666666, colorStroke: 0x999999 },
    },
    [PreProcessingTypes.GROUND_LAKE]: {
        standard: { color: 0x666666, colorStroke: 0x999999 },
        render: { color: 0x666666, colorStroke: 0x999999 },
    },
    [PreProcessingTypes.GROUND_RIVER]: {
        standard: { color: 0x666666, colorStroke: 0x999999 },
        render: { color: 0x666666, colorStroke: 0x999999 },
    },
    [PreProcessingTypes.GROUND_CUSTOM_TARGET]: {
        standard: { color: 0x555555, colorStroke: 0x777777 },
        render: { color: 0x555555, colorStroke: 0x777777 },
    },

    // Buildings:
    [PreProcessingTypes.SWISSTOPO_FLOOR]: {
        standard: { color: 0x666666, colorStroke: 0x3a3a3a },
        render: { color: 0x666666, colorStroke: 0x3a3a3a },
    },
    [PreProcessingTypes.SWISSTOPO_WALL]: {
        standard: { color: 0x777777, colorStroke: 0x3a3a3a },
        render: { color: 0x777777, colorStroke: 0x4f4f4f },
    },
    [PreProcessingTypes.SWISSTOPO_ROOF]: {
        standard: { color: 0x555555, colorStroke: 0x3a3a3a },
        render: { color: 0x555555, colorStroke: 0x3a3a3a },
    },

    // Trees:
    [PreProcessingTypes.TREES]: {
        standard: { color: 0x666666, colorStroke: 0x3a3a3a },
        render: { color: 0x666666, colorStroke: 0x3a3a3a },
    },

    // Custom Obstructions:
    [PreProcessingTypes.CUSTOM_BUILDINGS_ROOF]: {
        standard: { color: 0x4a4a4a, colorStroke: 0x111111 },
        render: { color: 0x4a4a4a, colorStroke: 0x111111 },
    },
    [PreProcessingTypes.CUSTOM_BUILDINGS_WALL]: {
        standard: { color: 0x6c6c6c, colorStroke: 0x111111 },
        render: { color: 0x6c6c6c, colorStroke: 0x111111 },
    },
    [PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR]: {
        standard: { color: 0x4a4a4a, colorStroke: 0x111111 },
        render: { color: 0x4a4a4a, colorStroke: 0x111111 },
    },

    // Project Sketch and Wind Station:
    ...CommonOptions,
};

const LightGreyColorScheme: AllColorScheme = {
    // Sky:
    SKY: 0xffffff,

    // Ground:
    [PreProcessingTypes.GROUND]: {
        standard: { color: 0xf7f7f7, colorStroke: 0xd9d9d9 },
        render: { color: 0xffffff, colorStroke: 0xe0e0e0 },
    },
    [PreProcessingTypes.GROUND_GREEN]: {
        standard: { color: 0xd9d9d9, colorStroke: 0xd9d9d9 },
        render: { color: 0xf3f3f3, colorStroke: 0xd9d9d9 },
    },
    [PreProcessingTypes.GROUND_LAKE]: {
        standard: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
        render: { color: 0xf3f3f3, colorStroke: 0xd9d9d9 },
    },
    [PreProcessingTypes.GROUND_RIVER]: {
        standard: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
        render: { color: 0xf3f3f3, colorStroke: 0xb7b7b7 },
    },
    [PreProcessingTypes.GROUND_CUSTOM_TARGET]: {
        standard: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
        render: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
    },

    // Buildings:
    [PreProcessingTypes.SWISSTOPO_FLOOR]: {
        standard: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
        render: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
    },
    [PreProcessingTypes.SWISSTOPO_WALL]: {
        standard: { color: 0xd9d9d9, colorStroke: 0x999999 },
        render: { color: 0xd9d9d9, colorStroke: 0x999999 },
    },
    [PreProcessingTypes.SWISSTOPO_ROOF]: {
        standard: { color: 0xc1c1c1, colorStroke: 0xa2a2a2 },
        render: { color: 0xc1c1c1, colorStroke: 0x666666 },
    },

    // Trees:
    [PreProcessingTypes.TREES]: {
        standard: { color: 0xbbbbbb, colorStroke: 0x939393 },
        render: { color: 0xbbbbbb, colorStroke: 0x666666 },
    },

    // Custom Obstructions:
    [PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR]: {
        standard: { color: 0xa1a1a1, colorStroke: 0x838383 },
        render: { color: 0xa1a1a1, colorStroke: 0x838383 },
    },
    [PreProcessingTypes.CUSTOM_BUILDINGS_WALL]: {
        standard: { color: 0xbfbfbf, colorStroke: 0x757575 },
        render: { color: 0xbfbfbf, colorStroke: 0x757575 },
    },
    [PreProcessingTypes.CUSTOM_BUILDINGS_ROOF]: {
        standard: { color: 0x969696, colorStroke: 0x7a7a7a },
        render: { color: 0x969696, colorStroke: 0x7a7a7a },
    },

    // Project Sketch and Wind Station:
    ...CommonOptions,
};

const LightGreyBlueGreenColorScheme: AllColorScheme = {
    ...LightGreyColorScheme,

    // Colored Lake and Rivers:
    [PreProcessingTypes.GROUND_LAKE]: {
        standard: { color: 0xcfe2f3, colorStroke: 0x9fc5e8 },
        render: { color: 0xcfe2f3, colorStroke: 0x9fc5e8 },
    },
    [PreProcessingTypes.GROUND_RIVER]: {
        standard: { color: 0xcfe2f3, colorStroke: 0x9fc5e8 },
        render: { color: 0xcfe2f3, colorStroke: 0x9fc5e8 },
    },
    // Colored Forests and Trees:
    [PreProcessingTypes.GROUND_GREEN]: {
        standard: { color: 0xd9ead3, colorStroke: 0x68c46e },
        render: { color: 0xd9ead3, colorStroke: 0x68c46e },
    },

    [PreProcessingTypes.TREES]: {
        standard: { color: 0xc3d1bc, colorStroke: 0x8ba37f },
        render: { color: 0xc3d1bc, colorStroke: 0x8ba37f },
    },
};

const LightGreyBlueGreen2ColorScheme: AllColorScheme = {
    ...LightGreyColorScheme,

    // Colored Lake and Rivers (from Colored mode):
    [PreProcessingTypes.GROUND_LAKE]: {
        standard: { color: 0x71a7dc, colorStroke: 0x3d85c6 },
        render: { color: 0x71a7dc, colorStroke: 0x3d85c6 },
    },
    [PreProcessingTypes.GROUND_RIVER]: {
        standard: { color: 0x71a7dc, colorStroke: 0x3d85c6 },
        render: { color: 0x71a7dc, colorStroke: 0x3d85c6 },
    },

    // Colored Forests and Trees (from Colored mode):
    [PreProcessingTypes.GROUND_GREEN]: {
        standard: { color: 0xd9ead3, colorStroke: 0x93c47d },
        render: { color: 0xd9ead3, colorStroke: 0x93c47d },
    },
    [PreProcessingTypes.TREES]: {
        standard: { color: 0x6aa84f, colorStroke: 0x38761d },
        render: { color: 0x6aa84f, colorStroke: 0x38761d },
    },
};

const WhiteColorScheme: AllColorScheme = {
    // Sky:
    SKY: 0xffffff,

    // Ground:
    [PreProcessingTypes.GROUND]: {
        standard: { color: 0xc9c9c9, colorStroke: 0xb7b7b7 },
        render: { color: 0xc9c9c9, colorStroke: 0xe0e0e0 },
    },
    [PreProcessingTypes.GROUND_GREEN]: {
        standard: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
        render: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
    },
    [PreProcessingTypes.GROUND_LAKE]: {
        standard: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
        render: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
    },
    [PreProcessingTypes.GROUND_RIVER]: {
        standard: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
        render: { color: 0xd9d9d9, colorStroke: 0xb7b7b7 },
    },
    [PreProcessingTypes.GROUND_CUSTOM_TARGET]: {
        standard: { color: 0xb7b7b7, colorStroke: 0x666666 },
        render: { color: 0xb7b7b7, colorStroke: 0x666666 },
    },

    // Buildings:
    [PreProcessingTypes.SWISSTOPO_FLOOR]: {
        standard: { color: 0xefefef, colorStroke: 0xefefef },
        render: { color: 0xefefef, colorStroke: 0xefefef },
    },
    [PreProcessingTypes.SWISSTOPO_WALL]: {
        standard: { color: 0xefefef, colorStroke: 0xb0b0b0 },
        render: { color: 0xffffff, colorStroke: 0xb0b0b0 },
    },
    [PreProcessingTypes.SWISSTOPO_ROOF]: {
        standard: { color: 0xe3e3e3, colorStroke: 0xcccccc },
        render: { color: 0xefefef, colorStroke: 0xcccccc },
    },

    // Trees:
    [PreProcessingTypes.TREES]: {
        standard: { color: 0xf3f3f3, colorStroke: 0xcccccc },
        render: { color: 0xf3f3f3, colorStroke: 0xcccccc },
    },

    // Custom Obstructions:
    [PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR]: {
        standard: { color: 0xd6d6d6, colorStroke: 0xd9d9d9 },
        render: { color: 0xf3f3f3, colorStroke: 0xd9d9d9 },
    },
    [PreProcessingTypes.CUSTOM_BUILDINGS_WALL]: {
        standard: { color: 0xc9c9c9, colorStroke: 0x828282 },
        render: { color: 0xf3f3f3, colorStroke: 0x828282 },
    },

    [PreProcessingTypes.CUSTOM_BUILDINGS_ROOF]: {
        standard: { color: 0xb8b8b8, colorStroke: 0x8f8f8f },
        render: { color: 0xb0b0b0, colorStroke: 0x8f8f8f },
    },

    // Project Sketch and Wind Station:
    ...CommonOptions,
};

const WhiteBlueGreenColorScheme: AllColorScheme = {
    ...WhiteColorScheme,

    // Colored Lake and Rivers:
    [PreProcessingTypes.GROUND_LAKE]: {
        standard: { color: 0xcfe2f3, colorStroke: 0x9fc5e8 },
        render: { color: 0xcfe2f3, colorStroke: 0x9fc5e8 },
    },
    [PreProcessingTypes.GROUND_RIVER]: {
        standard: { color: 0xcfe2f3, colorStroke: 0x9fc5e8 },
        render: { color: 0xcfe2f3, colorStroke: 0x9fc5e8 },
    },
    // Colored Forests and Trees:
    [PreProcessingTypes.GROUND_GREEN]: {
        standard: { color: 0xd9ead3, colorStroke: 0x68c46e },
        render: { color: 0xd9ead3, colorStroke: 0x68c46e },
    },

    [PreProcessingTypes.TREES]: {
        standard: { color: 0xc3d1bc, colorStroke: 0x8ba37f },
        render: { color: 0xc3d1bc, colorStroke: 0x8ba37f },
    },
};

// ============================================
// LEGACY ADAPTER (WILL EVENTUALLY BE REMOVED)
// ============================================

const LEGACY_BASE_VALUES = {
    GRID_FILL_H: [0, 70] as number[],
    GRID_FILL_S: 1,
    GRID_FILL_L: 0.5,
    MULTI_MODE_S: 0.6,
    MULTI_MODE_L: 0.5,
    CLOSEST_INTERSECTIONS: 0x0ff000,
    OTHER_INTERSECTIONS: 0xff0000,
    ROADS_H: 0.5,
    ROADS_S: 0,
    ROADS_L: [0.5, 0.1] as number[],
    LEGAL_BORDER: 0x575757,
    ANALYSIS_BORDER: 0x575757,
    BUILDABLE_BORDER: 0x575757,
    SITE_CENTER: 0x000000,
    SITE_BOX: 0x000000,
    CANVAS_BACKGROUND: '#000000',
};

function toLegacyColorScheme(colorScheme: AllColorScheme): ColorScheme {
    return {
        ...LEGACY_BASE_VALUES,
        SKY: colorScheme.SKY,
        GROUND: colorScheme[PreProcessingTypes.GROUND].standard.color,
        GROUND_STROKE: colorScheme[PreProcessingTypes.GROUND].standard.colorStroke,
        BUILDINGS_ROOF: colorScheme[PreProcessingTypes.SWISSTOPO_ROOF].standard.color,
        BUILDINGS_ROOF_STROKE: colorScheme[PreProcessingTypes.SWISSTOPO_ROOF].standard.colorStroke,
        BUILDINGS_WALL: colorScheme[PreProcessingTypes.SWISSTOPO_WALL].standard.color,
        BUILDINGS_WALL_STROKE: colorScheme[PreProcessingTypes.SWISSTOPO_WALL].standard.colorStroke,
        BUILDINGS_FLOOR: colorScheme[PreProcessingTypes.SWISSTOPO_FLOOR].standard.color,
        BUILDINGS_FLOOR_STROKE: colorScheme[PreProcessingTypes.SWISSTOPO_FLOOR].standard.colorStroke,
        BUILDINGS_PROJECT_SKETCH_ROOF: colorScheme[PreProcessingTypes.PROJECT_SKETCH_ROOF].standard.color,
        BUILDINGS_PROJECT_SKETCH_ROOF_STROKE: colorScheme[PreProcessingTypes.PROJECT_SKETCH_ROOF].standard.colorStroke,
        BUILDINGS_PROJECT_SKETCH_WALL: colorScheme[PreProcessingTypes.PROJECT_SKETCH_WALL].standard.color,
        BUILDINGS_PROJECT_SKETCH_WALL_STROKE: colorScheme[PreProcessingTypes.PROJECT_SKETCH_WALL].standard.colorStroke,
        BUILDINGS_PROJECT_SKETCH_FLOOR: colorScheme[PreProcessingTypes.PROJECT_SKETCH_FLOOR].standard.color,
        BUILDINGS_PROJECT_SKETCH_FLOOR_STROKE: colorScheme[PreProcessingTypes.PROJECT_SKETCH_FLOOR].standard.colorStroke,
        BUILDINGS_CUSTOM_OBSTRUCTION_ROOF: colorScheme[PreProcessingTypes.CUSTOM_BUILDINGS_ROOF].standard.color,
        BUILDINGS_CUSTOM_OBSTRUCTION_ROOF_STROKE: colorScheme[PreProcessingTypes.CUSTOM_BUILDINGS_ROOF].standard.colorStroke,
        BUILDINGS_CUSTOM_OBSTRUCTION_WALL: colorScheme[PreProcessingTypes.CUSTOM_BUILDINGS_WALL].standard.color,
        BUILDINGS_CUSTOM_OBSTRUCTION_WALL_STROKE: colorScheme[PreProcessingTypes.CUSTOM_BUILDINGS_WALL].standard.colorStroke,
        BUILDINGS_CUSTOM_OBSTRUCTION_FLOOR: colorScheme[PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR].standard.color,
        BUILDINGS_CUSTOM_OBSTRUCTION_FLOOR_STROKE: colorScheme[PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR].standard.colorStroke,
        TREES: colorScheme[PreProcessingTypes.TREES].standard.color,
        TREES_STROKE: colorScheme[PreProcessingTypes.TREES].standard.colorStroke,
        LAKE: colorScheme[PreProcessingTypes.GROUND_LAKE].standard.color,
        LAKE_STROKE: colorScheme[PreProcessingTypes.GROUND_LAKE].standard.colorStroke,
        FOREST: colorScheme[PreProcessingTypes.GROUND_GREEN].standard.color,
        FOREST_STROKE: colorScheme[PreProcessingTypes.GROUND_GREEN].standard.colorStroke,
        RIVER: colorScheme[PreProcessingTypes.GROUND_RIVER].standard.color,
        RIVER_STROKE: colorScheme[PreProcessingTypes.GROUND_RIVER].standard.colorStroke,
        CUSTOM_POLYGON: colorScheme[PreProcessingTypes.GROUND_CUSTOM_TARGET].standard.color,
        CUSTOM_POLYGON_STROKE: colorScheme[PreProcessingTypes.GROUND_CUSTOM_TARGET].standard.colorStroke,
        WIND_STATION_BOX: colorScheme.WIND_STATION_BOX,
    };
}

// =====================================
// COLOR SCHEME SELECTOR
// =====================================

export function selectColorScheme(colorModeOption: ColorModeOptions): ColorScheme {
    return toLegacyColorScheme(selectNewColorScheme(colorModeOption));
}

export function selectNewColorScheme(colorModeOption: ColorModeOptions): AllColorScheme {
    switch (colorModeOption) {
        case ColorModeOptions.Colored:
            return ColoredColorScheme;
        case ColorModeOptions.Dark:
            return DarkColorScheme;
        case ColorModeOptions.LightGrey:
            return LightGreyColorScheme;
        case ColorModeOptions.LightGreyBlueGreen:
            return LightGreyBlueGreenColorScheme;
        case ColorModeOptions.LightGreyBlueGreen2:
            return LightGreyBlueGreen2ColorScheme;
        case ColorModeOptions.WhiteMode:
            return WhiteColorScheme;
        case ColorModeOptions.WhiteBlueGreen:
            return WhiteBlueGreenColorScheme;
        // Backward compatibility for persisted values from older versions.
        case 'Light Grey blue water' as ColorModeOptions:
        case 'Light Grey green trees' as ColorModeOptions:
        case 'Light Grey 2 blue water' as ColorModeOptions:
        case 'Light Grey 2 green trees' as ColorModeOptions:
            return LightGreyBlueGreenColorScheme;
        case 'Light Grey 2' as ColorModeOptions:
            return LightGreyColorScheme;
        default:
            // Safe fallback for unexpected or legacy persisted values.
            return LightGreyBlueGreenColorScheme;
    }
}

// =====================================
// LINES COLOR SCHEMES
// =====================================

const baseLineColor: LineColor = {
    // Lines:
    Lines_SUN_H: [0.18, 0],
    Lines_VIEW_H: [0.7, 0.45],
    Lines_NOISE_H: [0.66, 0.33],
    Lines_WIND_H: [0.95, 0.7],
    Lines_S: 1,
    Lines_L: 0.5,

    OBSTRUCTED_LINES_H: 0,
    OBSTRUCTED_LINES_S: 0.9,
    OBSTRUCTED_LINES_L: 0.8,

    refresh: false,
    isBicolor: false,
};

export const lineMultiGradient: LineColor = {
    ...baseLineColor,
};

export const lineSingleGradient: LineColor = {
    ...baseLineColor,
    Lines_SUN_H: [0.49, 0.86],
    Lines_VIEW_H: [0.49, 0.86],
    Lines_NOISE_H: [0.49, 0.86],
    Lines_WIND_H: [0.45, 0.9],
};

export const lineFixedColors: LineColor = {
    ...baseLineColor,
    Lines_SUN_H: [0.0, 0.0],
    Lines_VIEW_H: [0.33, 0.33],
    Lines_NOISE_H: [0.5, 0.5],
    Lines_WIND_H: [0.67, 0.67],
};

export const lineBiColor: LineColor = {
    ...baseLineColor,
    Lines_SUN_H: [0.18, 0],
    Lines_VIEW_H: [0.7, 0.45],
    Lines_NOISE_H: [0.4, 0.2],
    Lines_WIND_H: [0.95, 0.7],
    isBicolor: true,
};

// =====================================
// LINES COLOR SCHEME SELECTOR
// =====================================

export enum LinesColorModeOptions {
    MultiGradient = 'Multi-Gradient',
    SingleGradient = 'Single-Gradient',
    FixedColors = 'Fixed-Color',
    BiColor = 'Bi-Color',
}

export function selectLinesColorMode(colorModeOption: LinesColorModeOptions): LineColor {
    switch (colorModeOption) {
        case LinesColorModeOptions.MultiGradient:
            return lineMultiGradient;
        case LinesColorModeOptions.SingleGradient:
            return lineSingleGradient;
        case LinesColorModeOptions.FixedColors:
            return lineFixedColors;
        case LinesColorModeOptions.BiColor:
            return lineBiColor;
    }
}
